import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesApi, usersApi, teamsApi } from '../lib/api-hooks';
import type { Category, RoutingRule, Prioritet } from '../lib/types';
import { clsx } from 'clsx';

const FARGE_FORSLAG = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

export default function CategoriesPage() {
  const qc = useQueryClient();
  const [showNewCat, setShowNewCat] = useState(false);
  const [editingRule, setEditingRule] = useState<string | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list() as Promise<Array<{ id: string; navn: string; epost: string }>>,
  });

  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: teamsApi.list,
  });

  const createCat = useMutation({
    mutationFn: categoriesApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  const updateCat = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof categoriesApi.update>[1] }) =>
      categoriesApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  const deleteCat = useMutation({
    mutationFn: categoriesApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  const createRule = useMutation({
    mutationFn: categoriesApi.createRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  const updateRule = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof categoriesApi.updateRule>[1] }) =>
      categoriesApi.updateRule(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  const deleteRule = useMutation({
    mutationFn: categoriesApi.removeRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  if (isLoading) {
    return <div className="flex h-full items-center justify-center text-slate-400">Laster…</div>;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:px-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Sakstyper & ruting</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Definer sakstyper og regler for auto-tildeling av saker basert på nøkkelord i e-postemne.
          </p>
        </div>
        <button onClick={() => setShowNewCat(true)} className="btn-primary text-sm">
          + Ny sakstype
        </button>
      </div>

      {showNewCat && (
        <NewCategoryForm
          onCreate={(data) => {
            createCat.mutate(data);
            setShowNewCat(false);
          }}
          onCancel={() => setShowNewCat(false)}
        />
      )}

      <div className="space-y-4">
        {categories?.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400 dark:border-slate-700">
            Ingen sakstyper ennå. Klikk "Ny sakstype" for å opprette.
          </div>
        )}

        {categories?.map((cat) => (
          <CategoryCard
            key={cat.id}
            category={cat}
            users={users ?? []}
            teams={teams ?? []}
            onToggleActive={() =>
              updateCat.mutate({ id: cat.id, data: { aktiv: !cat.aktiv } })
            }
            onDelete={() => {
              if (confirm(`Slette "${cat.navn}"? Alle rutingregler for denne slettes også.`)) {
                deleteCat.mutate(cat.id);
              }
            }}
            onAddRule={(data) => createRule.mutate({ ...data, categoryId: cat.id })}
            onUpdateRule={(id, data) => updateRule.mutate({ id, data })}
            onDeleteRule={(id) => deleteRule.mutate(id)}
            editingRule={editingRule}
            setEditingRule={setEditingRule}
          />
        ))}
      </div>
    </div>
  );
}

// ---------- Ny kategori form ----------
function NewCategoryForm({
  onCreate,
  onCancel,
}: {
  onCreate: (data: { navn: string; beskrivelse?: string; farge?: string }) => void;
  onCancel: () => void;
}) {
  const [navn, setNavn] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [farge, setFarge] = useState(FARGE_FORSLAG[0]);

  return (
    <div className="mb-4 rounded-xl border border-brand-200 bg-brand-50/50 p-4 dark:border-brand-500/30 dark:bg-brand-500/5">
      <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Ny sakstype</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Navn *</label>
          <input
            value={navn}
            onChange={(e) => setNavn(e.target.value)}
            placeholder="f.eks. IT-support, Faktura, HR"
            className="input text-sm"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Beskrivelse</label>
          <input
            value={beskrivelse}
            onChange={(e) => setBeskrivelse(e.target.value)}
            placeholder="Kort beskrivelse (valgfritt)"
            className="input text-sm"
          />
        </div>
      </div>
      <div className="mt-3">
        <label className="mb-1 block text-xs font-medium text-slate-500">Farge</label>
        <div className="flex flex-wrap gap-2">
          {FARGE_FORSLAG.map((f) => (
            <button
              key={f}
              onClick={() => setFarge(f)}
              className={clsx(
                'h-7 w-7 rounded-full transition-transform hover:scale-110',
                farge === f && 'ring-2 ring-offset-2 ring-slate-400',
              )}
              style={{ backgroundColor: f }}
            />
          ))}
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => onCreate({ navn, beskrivelse: beskrivelse || undefined, farge })}
          disabled={!navn.trim()}
          className="btn-primary text-sm"
        >
          Opprett
        </button>
        <button onClick={onCancel} className="btn-ghost text-sm">
          Avbryt
        </button>
      </div>
    </div>
  );
}

// ---------- Kategori-kort med rutingregler ----------
function CategoryCard({
  category,
  users,
  teams,
  onToggleActive,
  onDelete,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
  editingRule,
  setEditingRule,
}: {
  category: Category;
  users: Array<{ id: string; navn: string }>;
  teams: Array<{ id: string; navn: string }>;
  onToggleActive: () => void;
  onDelete: () => void;
  onAddRule: (data: {
    assigneeId?: string;
    teamId?: string;
    prioritet?: Prioritet;
    nokkelord?: string;
  }) => void;
  onUpdateRule: (id: string, data: Parameters<typeof categoriesApi.updateRule>[1]) => void;
  onDeleteRule: (id: string) => void;
  editingRule: string | null;
  setEditingRule: (id: string | null) => void;
}) {
  const [showAddRule, setShowAddRule] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <span
            className="inline-flex h-3 w-3 rounded-full"
            style={{ backgroundColor: category.farge }}
          />
          <div>
            <span className="font-medium text-slate-800 dark:text-slate-100">{category.navn}</span>
            {category.beskrivelse && (
              <span className="ml-2 text-sm text-slate-400">{category.beskrivelse}</span>
            )}
          </div>
          {!category.aktiv && (
            <span className="chip bg-slate-100 text-slate-500">Inaktiv</span>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={onToggleActive} className="btn-ghost text-xs" title="Aktiver/deaktiver">
            {category.aktiv ? 'Deaktiver' : 'Aktiver'}
          </button>
          <button onClick={onDelete} className="btn-ghost text-xs text-red-600" title="Slett">
            Slett
          </button>
        </div>
      </div>

      {/* Rutingregler */}
      <div className="px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Rutingregler ({category.routingRules?.length ?? 0})
          </span>
          <button onClick={() => setShowAddRule(!showAddRule)} className="btn-ghost text-xs">
            + Legg til regel
          </button>
        </div>

        {showAddRule && (
          <RuleForm
            users={users}
            teams={teams}
            onSubmit={(data) => {
              onAddRule(data);
              setShowAddRule(false);
            }}
            onCancel={() => setShowAddRule(false)}
          />
        )}

        <div className="space-y-2">
          {category.routingRules?.map((rule) => (
            <div key={rule.id}>
              {editingRule === rule.id ? (
                <RuleForm
                  users={users}
                  teams={teams}
                  initial={rule}
                  onSubmit={(data) => {
                    onUpdateRule(rule.id, data);
                    setEditingRule(null);
                  }}
                  onCancel={() => setEditingRule(null)}
                />
              ) : (
                <RuleRow
                  rule={rule}
                  onEdit={() => setEditingRule(rule.id)}
                  onDelete={() => {
                    if (confirm('Slette denne rutingregelen?')) onDeleteRule(rule.id);
                  }}
                />
              )}
            </div>
          ))}

          {(!category.routingRules || category.routingRules.length === 0) && !showAddRule && (
            <p className="py-2 text-center text-xs text-slate-400">
              Ingen regler. Legg til en regel for å auto-tildele saker av denne typen.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Regel-rad ----------
function RuleRow({
  rule,
  onEdit,
  onDelete,
}: {
  rule: RoutingRule;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/50">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {rule.nokkelord ? (
          <span className="chip bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
            🏷️ {rule.nokkelord}
          </span>
        ) : (
          <span className="chip bg-slate-200 text-slate-500">Standard (ingen nøkkelord)</span>
        )}
        {rule.assignee && (
          <span className="chip bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
            → {rule.assignee.navn}
          </span>
        )}
        {rule.team && (
          <span className="chip bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300">
            👥 {rule.team.navn}
          </span>
        )}
        {rule.prioritet && (
          <span className="chip bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
            {rule.prioritet}
          </span>
        )}
        {!rule.aktiv && <span className="chip bg-slate-100 text-slate-400">Inaktiv</span>}
      </div>
      <div className="flex gap-1">
        <button onClick={onEdit} className="btn-ghost text-xs">
          Endre
        </button>
        <button onClick={onDelete} className="btn-ghost text-xs text-red-600">
          Slett
        </button>
      </div>
    </div>
  );
}

// ---------- Regel-form (ny + rediger) ----------
function RuleForm({
  users,
  teams,
  initial,
  onSubmit,
  onCancel,
}: {
  users: Array<{ id: string; navn: string }>;
  teams: Array<{ id: string; navn: string }>;
  initial?: RoutingRule;
  onSubmit: (data: {
    assigneeId?: string;
    teamId?: string;
    prioritet?: Prioritet;
    nokkelord?: string;
  }) => void;
  onCancel: () => void;
}) {
  const [nokkelord, setNokkelord] = useState(initial?.nokkelord || '');
  const [assigneeId, setAssigneeId] = useState(initial?.assigneeId || '');
  const [teamId, setTeamId] = useState(initial?.teamId || '');
  const [prioritet, setPrioritet] = useState<Prioritet | ''>(initial?.prioritet || '');

  return (
    <div className="relative z-20 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            Nøkkelord i emnefelt (kommaseparert)
          </label>
          <input
            value={nokkelord}
            onChange={(e) => setNokkelord(e.target.value)}
            placeholder="f.eks. faktura, regning, betaling"
            className="input text-sm"
          />
          <p className="mt-1 text-xs text-slate-400">
            La stå tom for å bruke som standardregel for denne sakstypen.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Auto-tildel til agent</label>
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="select-input text-sm"
          >
            <option value="">Ingen</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.navn}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Eller tildel til team</label>
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="select-input text-sm"
          >
            <option value="">Ingen</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.navn}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Prioritet</label>
          <select
            value={prioritet}
            onChange={(e) => setPrioritet(e.target.value as Prioritet | '')}
            className="select-input text-sm"
          >
            <option value="">Standard</option>
            <option value="LAV">Lav</option>
            <option value="NORMAL">Normal</option>
            <option value="HOY">Høy</option>
            <option value="KRITISK">Kritisk</option>
          </select>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={() =>
            onSubmit({
              assigneeId: assigneeId || undefined,
              teamId: teamId || undefined,
              prioritet: (prioritet as Prioritet) || undefined,
              nokkelord: nokkelord || undefined,
            })
          }
          className="btn-primary text-sm"
        >
          {initial ? 'Lagre endringer' : 'Legg til regel'}
        </button>
        <button onClick={onCancel} className="btn-ghost text-sm">
          Avbryt
        </button>
      </div>
    </div>
  );
}
